/*
 * Grace Jacob cart drawer helpers
 * - <gj-cart-recommendations>: cross-sell in the cart drawer, based on the most recently added cart item.
 *   Fetches the cart-drawer section through the product recommendations endpoint (complementary + related
 *   in parallel), merges them (complementary first), drops duplicates and products already in the cart
 *   (done server side), and falls back to the configured collection when nothing is returned.
 * - Keeps free shipping progress bars in sync on cart:change (the message itself is handled by the theme).
 */
(() => {
  const cache = new Map();

  const fetchSection = async (productId, sectionId, limit, intent) => {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const url = `${root}recommendations/products?product_id=${encodeURIComponent(productId)}&limit=${limit}&section_id=${encodeURIComponent(sectionId)}&intent=${intent}`;
    const response = await fetch(url, { priority: 'low' });
    if (!response.ok) return null;
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  const cardsFrom = (doc, selector) => {
    const container = doc && doc.querySelector(selector);
    return container ? Array.from(container.querySelectorAll(':scope > .horizontal-product-card')) : [];
  };

  class GjCartRecommendations extends HTMLElement {
    connectedCallback() {
      const productId = this.getAttribute('product-id');
      const sectionId = this.getAttribute('section-id');
      if (!productId || !sectionId) return;

      this.key = `${productId}|${this.getAttribute('cart-key') || ''}`;

      // Drawer content is re-rendered on every cart change: render from cache synchronously to avoid flicker
      if (cache.has(this.key)) {
        const cached = cache.get(this.key);
        if (cached instanceof Promise) {
          cached.then((result) => this.isConnected && this.render(result));
        } else {
          this.render(cached);
        }
        return;
      }

      const promise = this.load(productId, sectionId)
        .then((result) => {
          cache.set(this.key, result);
          return result;
        })
        .catch(() => {
          cache.delete(this.key);
          return null;
        });

      cache.set(this.key, promise);
      promise.then((result) => this.isConnected && this.render(result));
    }

    async load(productId, sectionId) {
      const limit = parseInt(this.getAttribute('limit'), 10) || 4;
      const requestLimit = parseInt(this.getAttribute('request-limit'), 10) || limit;

      const [complementary, related] = await Promise.all([
        fetchSection(productId, sectionId, requestLimit, 'complementary').catch(() => null),
        fetchSection(productId, sectionId, requestLimit, 'related').catch(() => null)
      ]);

      const seen = new Set();
      const cards = [];

      [...cardsFrom(complementary, '#gj-cart-recs-payload'), ...cardsFrom(related, '#gj-cart-recs-payload')].forEach((card) => {
        const id = card.getAttribute('data-product-id');
        if (cards.length >= limit || (id && seen.has(id))) return;
        if (id) seen.add(id);
        cards.push(card.outerHTML);
      });

      if (cards.length > 0) {
        return { cards, fallback: false };
      }

      // Nothing recommended: the "related" response carries the fallback collection (rendered only in that case)
      const fallbackCards = cardsFrom(related, '#gj-cart-recs-fallback').slice(0, limit).map((card) => card.outerHTML);
      return fallbackCards.length > 0 ? { cards: fallbackCards, fallback: true } : null;
    }

    render(result) {
      const list = this.querySelector('[data-gj-recs-list]');
      if (!result || !list || result.cards.length === 0) {
        this.hidden = true;
        return;
      }

      if (result.fallback) {
        const title = this.querySelector('[data-gj-recs-title]');
        if (title && title.dataset.fallbackTitle) title.textContent = title.dataset.fallbackTitle;
      }

      list.innerHTML = result.cards.join('');
      this.hidden = false;
    }
  }

  if (!customElements.get('gj-cart-recommendations')) {
    customElements.define('gj-cart-recommendations', GjCartRecommendations);
  }

  // Free shipping progress: width + reached state (the theme element only updates the message text)
  document.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart || !Array.isArray(cart.items)) return;

    const itemsTotal = cart.items
      .filter((item) => item.requires_shipping)
      .reduce((sum, item) => sum + item.final_line_price, 0);
    const discounts = (cart.cart_level_discount_applications || [])
      .reduce((sum, discount) => sum + discount.total_allocated_amount, 0);
    const total = itemsTotal - discounts;

    document.querySelectorAll('free-shipping-bar.fsb').forEach((bar) => {
      const thresholdCents = parseFloat(bar.getAttribute('data-threshold-cents'));
      if (!thresholdCents) return;
      const fill = bar.querySelector('[data-fsb-fill]');
      const pct = Math.max(0, Math.min((total / thresholdCents) * 100, 100));
      if (fill) fill.style.width = `${pct}%`;
      bar.classList.toggle('fsb--reached', total >= thresholdCents);
    });
  });
})();
